import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import _ from 'lodash';

import LastViewedAtContext from 'contexts/LastViewedAtContext';
import isDefined from 'isDefined';
import Typography from 'kingpin/atoms/Typography';
import PopUpMenuChevron from './PopUpMenuChevron';
import PopUpMenu from 'kingpin/atoms/PopUpMenu';
import NavMenuPopup from './NavMenuPopup';
import LeftNavItem from '../../../Common/LeftNavItem';
import { V3AppsContext } from '../../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import RolesContext from '../../../../../../contexts/RolesContext';
import PERMISSIONS from '../../../../../../permissionDefinitions';
import Icon from '../../../../../../kingpin/atoms/Icon';

const Divider = styled.div`
  height: 1px;
  background: var(--color-border-secondary);
  margin: 8px 8px 6px 8px;
`;

const WorkspaceItem = ({ workspace }: { workspace: V3.Workspace }) => {
  const link = useMemo(() => {
    return `/workspaces/${workspace.urlSlug}`;
  }, [workspace.urlSlug]);

  if (!link) {
    return null;
  }

  return (
    <NavLink to={link}>
      <NavMenuPopup.ItemDiv>
        <Typography.Body type={'Label 14'} isEllipsis ellipsisPosition="right">
          {workspace.label}
        </Typography.Body>
      </NavMenuPopup.ItemDiv>
    </NavLink>
  );
};

const WorkspacesMenuPopup = () => {
  const { getLastViewedAt } = useContext(LastViewedAtContext);
  const { availableWorkspaces } = useContext(V3AppsContext);

  const workspacesWithLastViewedAt = useMemo((): {
    workspace: V3.Workspace;
    lastViewedAt: string;
  }[] => {
    return availableWorkspaces
      .map((p) => {
        const lastViewed = getLastViewedAt('workspace', p.id);
        if (!lastViewed) {
          return undefined;
        }

        return {
          workspace: p,
          lastViewedAt: lastViewed,
        };
      })
      .filter(isDefined);
  }, [availableWorkspaces, getLastViewedAt]);

  const sortedWorkspaces = useMemo(() => {
    return _.sortBy(workspacesWithLastViewedAt, 'lastViewedAt').reverse();
  }, [workspacesWithLastViewedAt]);

  const shownWorkspaces = useMemo(() => {
    if (sortedWorkspaces.length === 0) {
      return {
        workspaces: availableWorkspaces.slice(0, 5),
        isRecents: false,
      };
    }
    return {
      workspaces: sortedWorkspaces.map((ws) => ws.workspace).slice(0, 5),
      isRecents: true,
    };
  }, [availableWorkspaces, sortedWorkspaces]);

  return (
    <NavMenuPopup.PopupDiv>
      {shownWorkspaces.isRecents && (
        <div style={{ padding: '4px 8px' }}>
          <Typography.Body
            type={'Label xs'}
            color={'var(--color-text-tertiary)'}
          >
            Recent Workspaces
          </Typography.Body>
        </div>
      )}
      {shownWorkspaces.workspaces.map((workspace) => (
        <WorkspaceItem key={workspace.id} workspace={workspace} />
      ))}
      {shownWorkspaces.workspaces.length > 0 && <Divider />}
      <NavLink to={'/workspaces'}>
        <NavMenuPopup.ItemViewAllDiv>
          <Typography.Body type={'Body 14'} color={'var(--color-status-info)'}>
            View All Workspaces
          </Typography.Body>
          <Icon icon={'arrow-right'} color={'var(--color-status-info)'} />
        </NavMenuPopup.ItemViewAllDiv>
      </NavLink>
    </NavMenuPopup.PopupDiv>
  );
};

const WorkspacesMenu = () => {
  const { currentPermissions } = useContext(RolesContext);
  const { availableWorkspaces } = useContext(V3AppsContext);
  const { pathname } = useLocation();
  const isSelected = useMemo(() => {
    return pathname.includes('/workspaces');
  }, [pathname]);

  const isHidden = useMemo(() => {
    if (availableWorkspaces.length > 0) {
      return false;
    }

    if (currentPermissions.includes(PERMISSIONS.WORKSPACES.CREATE)) {
      return false;
    }
    return true;
  }, [availableWorkspaces.length, currentPermissions]);

  if (isHidden) {
    return null;
  }

  return (
    <PopUpMenu
      trigger={({ isOpen }: { isOpen: boolean }) => (
        <LeftNavItem isOpen={isOpen || isSelected}>
          <Typography.Body type={'Label 14'}>Workspaces</Typography.Body>
          <PopUpMenuChevron isOpen={isOpen} />
        </LeftNavItem>
      )}
      children={() => <WorkspacesMenuPopup />}
      position={'bottom-start'}
    />
  );
};

export default WorkspacesMenu;
